import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { usd } from "../lib/money";
import { BOOK_LABEL } from "./Performance";
import { Money, MoneySwitch, ScopeNote } from "./MoneyScope";

// CAPITAL AT WORK — "how much capital is being deployed and is it effective?"
// (Alan, 2026-08-19). One hero (% of equity deployed now vs. the window's
// average), one chart (% deployed per day, one line per book, 100% reference),
// three honest returns (on deployed / on equity / per $100 deployed-day), and a
// per-sleeve table with a utilisation meter. Colors: categorical per book,
// fixed order, validated for the dark surface (#12151c) — never cycled.

type Pt = { date: string; deployed: number; peak: number; equity: number | null; pct: number | null; pct_eod: number | null };
type Book = {
  book: string; label: string; money: string; points: Pt[];
  avg_pct: number | null; now_pct: number | null; peak_pct: number | null;
  budget: number; budget_pct: number | null; equity_history_from: string | null;
};
type Sleeve = {
  sleeve: string; avg_deployed: number; budget: number; avg_util_pct: number | null;
  days_active: number; days: number; days_over_80pct: number;
  pnl: number; realized: number; unrealized: number;
  return_on_deployed_pct: number | null; annualized_pct: number | null; pnl_per_100_deployed_day: number | null;
};
type Firm = {
  points: { date: string; deployed: number; equity: number; pct: number | null }[];
  now_pct: number | null; avg_pct: number | null; pnl: number; avg_deployed: number;
  return_on_deployed_pct: number | null; return_on_equity_pct: number | null; pnl_per_100_deployed_day: number | null;
};
type Resp = { period: string; days: string[]; firm: Firm | null; books: Book[]; sleeves: Sleeve[]; notes: string[]; is_paper: boolean; scope_books: string[] };

const PERIODS = ["30d", "90d", "all"] as const;
type Period = (typeof PERIODS)[number];
// Fixed categorical order + hues (validated: L band, chroma, CVD ΔE ≥ 11, contrast ≥ 3:1 on #12151c).
const SERIES: { key: string; color: string }[] = [
  { key: "paper", color: "#3d8ee6" },
  { key: "live", color: "#e06535" },
  { key: "paper_long_term", color: "#9a6fe8" },
  { key: "live_long_term", color: "#b58424" },
];
const SLEEVE_LABEL: Record<string, string> = { equity: "Equities", option: "Options", crypto: "Crypto", forex: "Forex", commodity: "Commodities" };

export default function CapitalAtWork({ book }: { book: string }) {
  const [period, setPeriod] = useState<Period>("90d");
  const [money, setMoney] = useState<Money>("real");
  const [data, setData] = useState<Resp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let live = true;
    setData(null);
    api.get<Resp>(`/api/performance/capital?period=${period}&book=${book}&money=${money}`)
      .then((r) => { if (live) { setData(r); setErr(null); } })
      .catch((e) => { if (live) setErr((e as Error).message); });
    return () => { live = false; };
  }, [period, book, money]);

  const f = data?.firm;
  const gap = f && f.return_on_deployed_pct != null && f.return_on_equity_pct != null
    ? f.return_on_deployed_pct - f.return_on_equity_pct : null;

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="label mb-0">Capital at work <span className="normal-case text-slate-500">— how much is deployed, and is it earning</span></h2>
          {data && <ScopeNote book={book} money={money} isPaper={data.is_paper} books={data.scope_books} />}
        </div>
        <MoneySwitch book={book} money={money} onChange={setMoney} />
        <div className="flex gap-1 text-xs font-mono">
          {PERIODS.map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-2 py-0.5 rounded border ${period === p ? "border-slate-400 text-slate-100" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
      {err && <div className="text-down text-sm mt-2">{err}</div>}
      {!data && !err && <div className="text-slate-600 text-sm mt-3">walking the ledger…</div>}
      {data && !f && <div className="text-slate-600 text-sm mt-3">nothing deployed in this scope yet</div>}
      {data && f && (
        <>
          {/* Hero + the three honest returns */}
          <div className="grid md:grid-cols-[auto_1fr] gap-5 mt-3 items-start">
            <div>
              <div className="text-xs text-slate-500">Of {book !== "all" ? "book" : data.is_paper ? "paper" : "live"} equity deployed <span className="text-slate-600">· today</span></div>
              <div className="text-5xl font-black font-mono leading-none mt-1 text-slate-100">
                {f.now_pct != null ? `${f.now_pct.toFixed(0)}%` : "—"}
              </div>
              <div className="text-xs text-slate-500 mt-1 font-mono">
                avg {f.avg_pct != null ? `${f.avg_pct.toFixed(0)}%` : "—"} over the window
                {f.now_pct != null && f.avg_pct != null && (
                  <span className={`ml-2 ${f.now_pct >= f.avg_pct ? "text-up" : "text-down"}`}>
                    {f.now_pct >= f.avg_pct ? "▲" : "▼"} {Math.abs(f.now_pct - f.avg_pct).toFixed(0)} pts
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Tile k="Return on deployed" v={pct(f.return_on_deployed_pct)} sub={`P&L ${usd(f.pnl)} ÷ avg deployed ${usd(f.avg_deployed)}`}
                    tone={(f.return_on_deployed_pct ?? 0) >= 0 ? "up" : "down"} />
              <Tile k="Return on equity" v={pct(f.return_on_equity_pct)} sub={gap != null ? `${gap.toFixed(1)} pts is the cost of idle capital` : "same P&L ÷ avg equity"}
                    tone={(f.return_on_equity_pct ?? 0) >= 0 ? "up" : "down"} />
              <Tile k="Per $100 deployed-day" v={f.pnl_per_100_deployed_day != null ? usd(f.pnl_per_100_deployed_day) : "—"} sub="P&L earned per $100 at work per day"
                    tone={(f.pnl_per_100_deployed_day ?? 0) >= 0 ? "up" : "down"} />
            </div>
          </div>

          {/* The chart: % of each book's equity deployed, per day (intraday peak) */}
          <div className="mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-slate-400">% of book equity deployed <span className="text-slate-600">· intraday peak, per day</span></div>
              <div className="flex gap-3 text-[11px]">
                {data.books.map((b) => {
                  const c = SERIES.find((s) => s.key === b.book)?.color ?? "#94a3b8";
                  const off = hidden[b.book];
                  return (
                    <button key={b.book} onClick={() => setHidden({ ...hidden, [b.book]: !off })}
                            className={`flex items-center gap-1.5 ${off ? "opacity-40" : ""}`}>
                      <span className="inline-block w-3 h-0.5 rounded" style={{ background: c }} />
                      <span className="text-slate-300">{BOOK_LABEL[b.book] ?? b.label}</span>
                      <span className="text-slate-500 font-mono">{b.now_pct != null ? `${b.now_pct.toFixed(0)}%` : "—"}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <DeployedChart books={data.books.filter((b) => !hidden[b.book])} days={data.days} />
          </div>

          {/* Per book: the numbers behind the lines */}
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-[11px] font-mono">
              <thead><tr className="text-left text-slate-500 uppercase text-[9px]">
                <th className="py-0.5">Book</th><th>Now</th><th>Avg</th><th>Peak</th><th>Budget (dial)</th><th>Budget ÷ equity</th><th>All-venue equity since</th>
              </tr></thead>
              <tbody>
                {data.books.map((b) => {
                  const c = SERIES.find((s) => s.key === b.book)?.color ?? "#94a3b8";
                  return (
                    <tr key={b.book} className="border-t border-slate-800">
                      <td className="py-0.5 text-slate-300"><span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: c }} />{BOOK_LABEL[b.book] ?? b.label}</td>
                      <td>{pct(b.now_pct, 0)}</td><td>{pct(b.avg_pct, 0)}</td><td>{pct(b.peak_pct, 0)}</td>
                      <td className="text-slate-400">{usd(b.budget)}</td>
                      <td className={b.budget_pct != null && b.budget_pct > 100 ? "text-gold" : ""}>{pct(b.budget_pct, 0)}</td>
                      <td className="text-slate-500">{b.equity_history_from ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Per sleeve: is the deployed money earning? */}
          <div className="mt-4">
            <div className="text-xs text-slate-400 mb-1">By sleeve <span className="text-slate-600">· utilisation of budget and return on what was deployed</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono">
                <thead><tr className="text-left text-slate-500 uppercase text-[9px]">
                  <th className="py-0.5">Sleeve</th><th className="min-w-[10rem]">Utilisation</th><th>Avg deployed</th><th>Days &gt;80%</th><th>P&amp;L</th><th>On deployed</th><th>Annualised</th><th>Per $100-day</th>
                </tr></thead>
                <tbody>
                  {data.sleeves.map((s) => (
                    <tr key={s.sleeve} className="border-t border-slate-800">
                      <td className="py-1 text-slate-300">{SLEEVE_LABEL[s.sleeve] ?? s.sleeve}</td>
                      <td><Meter pct={s.avg_util_pct} label={s.budget ? `${pct(s.avg_util_pct, 0)} of ${usd(s.budget)}` : "no budget"} /></td>
                      <td className="text-slate-400">{usd(s.avg_deployed)}</td>
                      <td>{s.days_over_80pct}/{s.days}</td>
                      <td className={s.pnl >= 0 ? "text-up" : "text-down"}>{usd(s.pnl)}</td>
                      <td className={(s.return_on_deployed_pct ?? 0) >= 0 ? "text-up" : "text-down"}>{pct(s.return_on_deployed_pct)}</td>
                      <td className="text-slate-400">{s.days_active >= 10 ? pct(s.annualized_pct, 0) : <span className="text-slate-600" title="fewer than 10 active days — annualising would be noise">—</span>}</td>
                      <td className={(s.pnl_per_100_deployed_day ?? 0) >= 0 ? "text-up" : "text-down"}>{s.pnl_per_100_deployed_day != null ? usd(s.pnl_per_100_deployed_day) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <details className="mt-3 text-[10px] text-slate-600">
            <summary className="cursor-pointer">how these numbers are built</summary>
            <ul className="list-disc ml-4 mt-1 space-y-0.5">{data.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
          </details>
        </>
      )}
    </div>
  );
}

function pct(v: number | null | undefined, d = 1): string {
  return v == null ? "—" : `${v >= 0 ? "" : "−"}${Math.abs(v).toFixed(d)}%`;
}

function Tile({ k, v, sub, tone }: { k: string; v: string; sub?: string; tone?: "up" | "down" }) {
  return (
    <div className="rounded border border-slate-800 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
      <div className={`text-xl font-bold font-mono ${tone === "up" ? "text-up" : tone === "down" ? "text-down" : "text-slate-200"}`}>{v}</div>
      {sub && <div className="text-[10px] text-slate-600 leading-snug">{sub}</div>}
    </div>
  );
}

// Utilisation meter: fill carries the state, the unfilled track is a lighter
// step of the same ramp, the value sits beside it in a text token.
function Meter({ pct: p, label }: { pct: number | null; label: string }) {
  const v = Math.max(0, Math.min(100, p ?? 0));
  const fill = p == null ? "#334155" : v >= 80 ? "#33e07a" : v >= 40 ? "#b58424" : "#3d8ee6";
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-28 rounded-sm overflow-hidden" style={{ background: "rgba(148,163,184,0.15)" }}>
        <div className="h-full rounded-sm" style={{ width: `${v}%`, background: fill }} />
      </div>
      <span className="text-slate-400">{label}</span>
    </div>
  );
}

function DeployedChart({ books, days }: { books: Book[]; days: string[] }) {
  const W = 760, H = 210, padL = 36, padR = 84, padT = 10, padB = 22;
  const ref = useRef<SVGSVGElement>(null);
  const [hi, setHi] = useState<number | null>(null);
  const n = days.length;
  const maxPct = useMemo(() => {
    const vals = books.flatMap((b) => b.points.map((p) => p.pct ?? 0));
    return Math.max(100, Math.ceil((Math.max(0, ...vals) * 1.05) / 25) * 25);
  }, [books]);
  const x = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
  const y = (v: number) => padT + (1 - v / maxPct) * (H - padT - padB);
  const ticks = useMemo(() => {
    const step = maxPct <= 100 ? 25 : maxPct <= 200 ? 50 : 100;
    const out: number[] = [];
    for (let v = 0; v <= maxPct; v += step) out.push(v);
    return out;
  }, [maxPct]);
  const series = books.map((b) => {
    const c = SERIES.find((s) => s.key === b.book)?.color ?? "#94a3b8";
    const segs: string[] = [];
    let cur: string[] = [];
    b.points.forEach((p, i) => {
      if (p.pct == null) { if (cur.length) segs.push(cur.join(" ")); cur = []; return; }
      cur.push(`${x(i)},${y(p.pct)}`);
    });
    if (cur.length) segs.push(cur.join(" "));
    const lastIdx = (() => { for (let i = b.points.length - 1; i >= 0; i--) if (b.points[i].pct != null) return i; return -1; })();
    return { b, c, segs, lastIdx };
  });
  // end-labels: nudge apart only if they would collide (>10px apart otherwise leave)
  const ends = series.filter((s) => s.lastIdx >= 0).map((s) => ({ s, yy: y(s.b.points[s.lastIdx].pct!) }))
    .sort((a, b) => a.yy - b.yy);
  for (let i = 1; i < ends.length; i++) if (ends[i].yy - ends[i - 1].yy < 11) ends[i].yy = ends[i - 1].yy + 11;

  if (n === 0) return <div className="text-slate-600 text-sm py-6 text-center">no ledger history in this window</div>;
  const dateLbl = (i: number) => days[i]?.slice(5).replace("-", "/") ?? "";
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.round(((px - padL) / (W - padL - padR)) * (n - 1));
    setHi(Math.max(0, Math.min(n - 1, i)));
  };
  return (
    <div className="relative">
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} className="w-full mt-1" style={{ maxHeight: 240 }}
           onMouseMove={onMove} onMouseLeave={() => setHi(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke={t === 100 ? "#475569" : "#1f2937"} strokeWidth={1} />
            <text x={padL - 6} y={y(t) + 3} fontSize={9} textAnchor="end" fill="#64748b" fontFamily="ui-monospace, monospace">{t}%</text>
          </g>
        ))}
        <text x={padL + 4} y={y(100) + 10} fontSize={8} fill="#64748b" fontFamily="ui-monospace, monospace">fully deployed</text>
        {[0, Math.floor((n - 1) / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i).map((i) => (
          <text key={i} x={x(i)} y={H - 6} fontSize={9} textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"} fill="#64748b" fontFamily="ui-monospace, monospace">{dateLbl(i)}</text>
        ))}
        {series.map(({ b, c, segs }) => segs.map((pts, k) => (
          <polyline key={`${b.book}-${k}`} points={pts} fill="none" stroke={c} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        )))}
        {ends.map(({ s, yy }) => {
          const p = s.b.points[s.lastIdx];
          return (
            <g key={s.b.book}>
              <circle cx={x(s.lastIdx)} cy={y(p.pct!)} r={4} fill={s.c} stroke="#12151c" strokeWidth={2} />
              {Math.abs(yy - y(p.pct!)) > 1 && <line x1={x(s.lastIdx) + 5} y1={y(p.pct!)} x2={x(s.lastIdx) + 14} y2={yy} stroke="#475569" strokeWidth={1} />}
              <text x={x(s.lastIdx) + 16} y={yy + 3} fontSize={10} fill="#cbd5e1" fontFamily="ui-monospace, monospace">
                {p.pct!.toFixed(0)}% <tspan fill="#64748b">{(BOOK_LABEL[s.b.book] ?? s.b.label).replace(" · ", "·")}</tspan>
              </text>
            </g>
          );
        })}
        {hi != null && (
          <g>
            <line x1={x(hi)} x2={x(hi)} y1={padT} y2={H - padB} stroke="#64748b" strokeWidth={1} />
            {series.map(({ b, c }) => {
              const p = b.points[hi];
              return p?.pct != null ? <circle key={b.book} cx={x(hi)} cy={y(p.pct)} r={4} fill={c} stroke="#12151c" strokeWidth={2} /> : null;
            })}
          </g>
        )}
      </svg>
      {hi != null && (
        <div className="absolute top-0 text-[11px] font-mono rounded border border-slate-700 bg-[#0f1218] px-2 py-1.5 pointer-events-none shadow"
             style={{ left: `${Math.min(78, Math.max(0, (x(hi) / W) * 100))}%` }}>
          <div className="text-slate-400 mb-0.5">{days[hi]}</div>
          {series.map(({ b, c }) => {
            const p = b.points[hi];
            return (
              <div key={b.book} className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: c }} />
                <span className="text-slate-300 w-24 truncate">{BOOK_LABEL[b.book] ?? b.label}</span>
                <span className="text-slate-100 w-12 text-right">{p?.pct != null ? `${p.pct.toFixed(0)}%` : "—"}</span>
                <span className="text-slate-500">{p ? `${usd(p.peak)} of ${p.equity != null ? usd(p.equity) : "—"}` : ""}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
